import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, ClipboardCheck, Lightbulb, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { notifyAlertsChanged } from "@/lib/useAlertCount";
import { AskEntry } from "@/components/conecta/AskEntry";
import { FoundersCounter } from "@/components/red/FoundersCounter";
import { RedShortcuts } from "@/components/red/NotifBell";
import { AgreementsSection, AiSummaryCard, StalledSection, StepsSection } from "@/components/conecta/HoySections";

const Tile = ({ to, icon: Icon, label, value, testid, alert }) => (
  <Link to={to} data-testid={testid} className="c-card p-3 flex flex-col gap-1 active:scale-[0.98] transition-transform">
    <Icon className={`w-4 h-4 ${alert ? "text-red-500" : "c-accent"}`} />
    <span data-testid={`${testid}-value`} className="font-heading text-2xl font-extrabold tabular-nums">{value}</span>
    <span className="text-[11px] c-text-2 leading-tight">{label}</span>
  </Link>
);

export default function HoyPage() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/hoy").then((r) => { setD(r.data); notifyAlertsChanged(); }).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin c-muted" /></div>;
  const greeting = new Date().getHours() < 12 ? "Buenos días" : new Date().getHours() < 19 ? "Buenas tardes" : "Buenas noches";
  return (
    <div data-testid="hoy-page" className="space-y-3">
      <p className="text-sm c-text-2">{greeting}, {user.name.split(" ")[0]} · {new Date(d.date + "T12:00").toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}</p>
      <RedShortcuts />
      <AskEntry testid="hoy-ask-entry" />
      <FoundersCounter />
      <div className="grid grid-cols-3 gap-2">
        <Tile to="/conecta/alertas" icon={Bell} label="Alertas sin leer" value={d.alerts.unread} testid="hoy-alerts-tile" alert={d.alerts.unread > 0} />
        <Tile to="/conecta/revisar" icon={ClipboardCheck} label="Por revisar" value={d.review_count} testid="hoy-review-tile" />
        <Tile to="/conecta/necesidades" icon={Lightbulb} label="Necesidades abiertas" value={d.open_needs} testid="hoy-needs-tile" />
      </div>
      {d.alerts.top.length > 0 && (
        <ul data-testid="hoy-alerts-list" className="c-card p-3 space-y-1.5">
          {d.alerts.top.map((a) => <li key={a.id} className="text-xs"><span className="font-semibold c-accent">{a.kind_label}:</span> {a.title}</li>)}
        </ul>
      )}
      <StepsSection steps={d.steps} onChanged={load} />
      <StalledSection items={d.stalled} days={d.stalled_days} />
      <AgreementsSection a={d.agreements} />
      <AiSummaryCard />
    </div>
  );
}
